// ========================================
// pages/HowItWorks.js - Detailed Process Guide
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 11, 2026
// 
// PURPOSE: Comprehensive step-by-step guide explaining how Agyapadie Vault works
// 
// ASSETS USED (from your inventory):
// - /images/symbols/golden-stool.png - Hero badge icon
// - /images/hero/step1-create.jpg - Step 1 image
// - /images/hero/step2-assets.jpg - Step 2 image
// - /images/hero/step3-trustees.jpg - Step 3 image
// - /images/hero/step4-verify.jpg - Step 4 image
// - /images/icons/feature-security.png - Security icon
// - /images/icons/feature-tradition.png - Tradition icon
// - /images/icons/feature-fraud.png - Fraud protection icon
// - /images/icons/feature-trustees.png - Multi-trustee icon
// - /images/icons/quote-mark.png - Testimonial quote icon
// - /images/hero/customer1.jpg, customer2.jpg, customer3.jpg - Testimonial photos
// ========================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const HowItWorks = () => {
  // ========================================
  // STATE FOR FAQ ACCORDION
  // ========================================
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // ========================================
  // FAQ DATA
  // ========================================
  const faqs = [
    {
      q: "How long does it take to set up my account?",
      a: "Setting up your account takes less than 5 minutes. You'll need to provide your name, email, phone number, and date of birth. Email verification adds another minute or two."
    },
    {
      q: "Do my trustees need to create accounts?",
      a: "Yes. When you invite a trustee, they receive an email invitation. They must create an account (or log in if they already have one) to accept the trustee role and respond to death verification requests."
    },
    {
      q: "What happens if none of my trustees respond?",
      a: "Trustees receive monthly emails for up to 1 year. If all trustees remain unresponsive after 1 year, they are removed. If no trustees remain and you become dormant (90 days without clicking 'I'm Alive'), assets are released directly to your beneficiaries."
    },
    {
      q: "Can I change my beneficiaries or trustees after setting them up?",
      a: "Yes, at any time while you are alive. You can add, remove, or modify Global Next of Kin, Trustees, and External Beneficiaries from your dashboard."
    },
    {
      q: "What types of assets can I store?",
      a: "You can store bank account details, real estate information, vehicle documents, investment portfolios, cryptocurrency wallets, digital assets, insurance policies, and any other important documents your loved ones need to know about."
    }
  ];

  return (
    <div className="how-it-works-page">
      
      {/* ======================================== */}
      {/* SECTION 1: HERO */}
      {/* ======================================== */}
      <section className="hiw-hero">
        <div className="hiw-hero-overlay"></div>
        <div className="hiw-container">
          <div className="hiw-hero-content">
            <div className="hiw-badge">
              <img src="/images/symbols/golden-stool.png" alt="Golden Stool" className="hiw-badge-icon" />
              <span>Step-by-Step Guide</span>
            </div>
            <h1 className="hiw-hero-title">
              How <span className="gold">Agyapadie Vault</span> Works
            </h1>
            <p className="hiw-hero-subtitle">
              A simple, secure process to protect your legacy and ensure your loved ones 
              receive what you've worked for.
            </p>
            <div className="hiw-hero-buttons">
              <Link to="/register" className="hiw-btn-primary">Get Started Now</Link>
              <Link to="/faq" className="hiw-btn-secondary">View Full FAQ</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 2: 5-STEP PROCESS */}
      {/* ======================================== */}
      <section className="hiw-steps">
        <div className="hiw-container">
          <h2 className="hiw-section-title">The <span className="gold">5-Step Process</span></h2>
          <p className="hiw-section-subtitle">
            From signup to asset release - here's everything you need to know
          </p>
          
          <div className="hiw-steps-grid">
            
            {/* Step 1 - Create Account */}
            <div className="hiw-step-card">
              <div className="hiw-step-number">01</div>
              <img src="/images/hero/step1-create.jpg" alt="Create Account" className="hiw-step-img" />
              <h3 className="hiw-step-title">Create Your Account</h3>
              <p className="hiw-step-desc">
                Sign up with your name, email, phone number, and date of birth. 
                Verify your email address to activate your account. All plans start 
                with the Free tier - you can upgrade anytime.
              </p>
              <div className="hiw-step-tip">
                <strong>💡 Tip:</strong> Use an email you check regularly - this is how we contact you about check-ins.
              </div>
            </div>
            
            {/* Step 2 - Add Global NOKs */}
            <div className="hiw-step-card">
              <div className="hiw-step-number">02</div>
              <img src="/images/hero/step2-assets.jpg" alt="Add Next of Kin" className="hiw-step-img" />
              <h3 className="hiw-step-title">Add Global Next of Kin</h3>
              <p className="hiw-step-desc">
                Add your Global Next of Kin (NOKs) - these are your primary beneficiaries. 
                You must have at least 1 Global NOK before creating any assets. 
                Every asset is automatically linked to all your NOKs by default.
              </p>
              <div className="hiw-step-tip">
                <strong>💡 Tip:</strong> You can unlink specific NOKs per asset if you want different beneficiaries for different assets.
              </div>
            </div>
            
            {/* Step 3 - Add Assets */}
            <div className="hiw-step-card">
              <div className="hiw-step-number">03</div>
              <img src="/images/hero/step3-trustees.jpg" alt="Add Assets" className="hiw-step-img" />
              <h3 className="hiw-step-title">Add Your Assets</h3>
              <p className="hiw-step-desc">
                Add bank accounts, properties, vehicles, investments, digital assets, 
                and important documents. Each asset gets a unique certificate with 
                a cryptographic hash for integrity verification.
              </p>
              <div className="hiw-step-tip">
                <strong>💡 Tip:</strong> Every asset must have at least one beneficiary 
                (either Global NOK or External Beneficiary).
              </div>
            </div>
            
            {/* Step 4 - Appoint Trustees */}
            <div className="hiw-step-card">
              <div className="hiw-step-number">04</div>
              <img src="/images/hero/step4-verify.jpg" alt="Appoint Trustees" className="hiw-step-img" />
              <h3 className="hiw-step-title">Appoint Your Trustees</h3>
              <p className="hiw-step-desc">
                Invite trusted people to serve as trustees. They will verify your death 
                if you become dormant. Trustees receive email invitations and must 
                accept to become active. Minimum 1 trustee required.
              </p>
              <div className="hiw-step-tip">
                <strong>💡 Tip:</strong> Choose people who are reliable and will respond to emails - family members, lawyers, or close friends.
              </div>
            </div>
            
            {/* Step 5 - External Beneficiaries */}
            <div className="hiw-step-card">
              <div className="hiw-step-number">05</div>
              <img src="/images/icons/external-icon.png" alt="External Beneficiaries" className="hiw-step-img" />
              <h3 className="hiw-step-title">Add External Beneficiaries</h3>
              <p className="hiw-step-desc">
                For paid plans (Premium, Business, Enterprise), you can add External 
                Beneficiaries PER ASSET. These are additional beneficiaries outside 
                your Global NOKs. Free plan does not support external beneficiaries.
              </p>
              <div className="hiw-step-tip">
                <strong>💡 Tip:</strong> External beneficiaries are perfect for assets you want to leave to specific people not in your Global NOKs.
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 3: FEATURES (Using your icons) */}
      {/* ======================================== */}
      <section className="hiw-features">
        <div className="hiw-container">
          <h2 className="hiw-section-title">Why Choose <span className="gold">Agyapadie</span></h2>
          <div className="hiw-features-grid">
            <div className="hiw-feature-card">
              <img src="/images/icons/feature-security.png" alt="Security" className="hiw-feature-icon" />
              <h3>Bank-Grade Security</h3>
              <p>Your assets are encrypted and protected with cryptographic proofs.</p>
            </div>
            <div className="hiw-feature-card">
              <img src="/images/icons/feature-tradition.png" alt="Tradition" className="hiw-feature-icon" />
              <h3>Traditional Values</h3>
              <p>Designed for Ghanaian family structures and inheritance customs.</p>
            </div>
            <div className="hiw-feature-card">
              <img src="/images/icons/feature-fraud.png" alt="Fraud Protection" className="hiw-feature-icon" />
              <h3>Proof Against Fraud</h3>
              <p>Cryptographic proofs protect your family from dishonest claims.</p>
            </div>
            <div className="hiw-feature-card">
              <img src="/images/icons/feature-trustees.png" alt="Trustees" className="hiw-feature-icon" />
              <h3>Multi-Trustee System</h3>
              <p>Multiple trustees verify death, ensuring no single point of failure.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 4: WHAT HAPPENS WHEN YOU PASS */}
      {/* ======================================== */}
      <section className="hiw-death-flow">
        <div className="hiw-container">
          <h2 className="hiw-section-title">What Happens <span className="gold">When You Pass Away</span></h2>
          <p className="hiw-section-subtitle">
            A secure, automated process ensures your assets reach the right people
          </p>
          
          <div className="hiw-flow-grid">
            
            <div className="hiw-flow-card">
              <div className="hiw-flow-step">Step 1</div>
              <div className="hiw-flow-icon">💤</div>
              <h3>90 Days of Dormancy</h3>
              <p>
                You must click the <strong>"I'm Alive"</strong> button on your dashboard 
                regularly. If you don't click it for 90 days, the system marks you as dormant.
              </p>
            </div>
            
            <div className="hiw-flow-arrow">→</div>
            
            <div className="hiw-flow-card">
              <div className="hiw-flow-step">Step 2</div>
              <div className="hiw-flow-icon">📧</div>
              <h3>Trustees Are Notified</h3>
              <p>
                All your trustees receive emails asking them to verify whether you are 
                alive or deceased. They receive monthly reminders until they respond.
              </p>
            </div>
            
            <div className="hiw-flow-arrow">→</div>
            
            <div className="hiw-flow-card">
              <div className="hiw-flow-step">Step 3</div>
              <div className="hiw-flow-icon">✅</div>
              <h3>Trustees Confirm Death</h3>
              <p>
                When enough trustees confirm you are deceased (based on your chosen 
                verification threshold), your account is marked as deceased.
              </p>
            </div>
            
          </div>
          
          <div className="hiw-flow-grid second-row">
            
            <div className="hiw-flow-card">
              <div className="hiw-flow-step">Step 4</div>
              <div className="hiw-flow-icon">📨</div>
              <h3>Beneficiaries Are Notified</h3>
              <p>
                All beneficiaries (Global NOKs and External Beneficiaries) receive 
                email notifications with claim links for the assets they inherit.
              </p>
            </div>
            
            <div className="hiw-flow-arrow">→</div>
            
            <div className="hiw-flow-card">
              <div className="hiw-flow-step">Step 5</div>
              <div className="hiw-flow-icon">🏦</div>
              <h3>Assets Are Released</h3>
              <p>
                Beneficiaries create accounts (or log in) using the same email that 
                received the notification. Inherited assets appear in their dashboard.
              </p>
            </div>
            
            <div className="hiw-flow-arrow">→</div>
            
            <div className="hiw-flow-card">
              <div className="hiw-flow-step">Step 6</div>
              <div className="hiw-flow-icon">📋</div>
              <h3>Unclaimed Assets Registry</h3>
              <p>
                If no beneficiary claims an asset, it moves to the Unclaimed Assets 
                Registry. Our field team attempts to locate family members.
              </p>
            </div>
            
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 5: TESTIMONIALS (Using your assets) */}
      {/* ======================================== */}
      <section className="hiw-testimonials">
        <div className="hiw-container">
          <h2 className="hiw-section-title">Trusted by <span className="gold">Ghanaian Families</span></h2>
          <div className="hiw-testimonial-grid">
            
            <div className="hiw-testimonial-card">
              <img src="/images/icons/quote-mark.png" alt="Quote" className="hiw-quote-icon" />
              <p>"Agyapadie gave me peace of mind knowing my children will receive what I've worked for."</p>
              <div className="hiw-author">
                <img src="/images/hero/customer1.jpg" alt="Nana Akua Mensah" />
                <div>
                  <h4>Nana Appiah Mensah</h4>
                  <p>Business Owner, Accra</p>
                </div>
              </div>
            </div>
            
            <div className="hiw-testimonial-card">
              <img src="/images/icons/quote-mark.png" alt="Quote" className="hiw-quote-icon" />
              <p>"The cryptographic proof feature saved my family from a bank dispute. Invaluable."</p>
              <div className="hiw-author">
                <img src="/images/hero/customer2.jpg" alt="Kwame Asante" />
                <div>
                  <h4>Kwame Asante</h4>
                  <p>Entrepreneur, Kumasi</p>
                </div>
              </div>
            </div>
            
            <div className="hiw-testimonial-card">
              <img src="/images/icons/quote-mark.png" alt="Quote" className="hiw-quote-icon" />
              <p>"Finally, a digital vault that respects our traditions while using modern technology."</p>
              <div className="hiw-author">
                <img src="/images/hero/customer3.jpg" alt="Adwoa Boateng" />
                <div>
                  <h4>Adwoa Boateng</h4>
                  <p>Lawyer, Tema</p>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 6: FAQ */}
      {/* ======================================== */}
      <section className="hiw-faq">
        <div className="hiw-container">
          <h2 className="hiw-section-title">Frequently Asked <span className="gold">Questions</span></h2>
          <p className="hiw-section-subtitle">
            Common questions about how Agyapadie Vault works
          </p>
          
          <div className="hiw-faq-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="hiw-faq-item">
                <button 
                  className="hiw-faq-question"
                  onClick={() => toggleFaq(index)}
                >
                  <span>{faq.q}</span>
                  <span className="hiw-faq-icon">{openFaq === index ? '−' : '+'}</span>
                </button>
                <div className={`hiw-faq-answer ${openFaq === index ? 'open' : ''}`}>
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="hiw-faq-more">
            <Link to="/faq" className="hiw-faq-link">View All FAQs →</Link>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 7: CALL TO ACTION */}
      {/* ======================================== */}
      <section className="hiw-cta">
        <div className="hiw-container">
          <div className="hiw-cta-content">
            <h2 className="hiw-cta-title">Ready to Protect Your Legacy?</h2>
            <p className="hiw-cta-subtitle">
              Join thousands of Ghanaian families who trust Agyapadie Vault.
              Start with our Free plan - no credit card required.
            </p>
            <div className="hiw-cta-buttons">
              <Link to="/register" className="hiw-cta-primary">Create Free Account</Link>
              <Link to="/#plans" className="hiw-cta-secondary">View Pricing Plans</Link>
            </div>
            <p className="hiw-cta-note">
              Free plan includes 2 Global NOKs, 10 assets, and 2 trustees.
            </p>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* STYLES */}
      {/* ======================================== */}
      <style dangerouslySetInnerHTML={{ __html: `
        
        /* How It Works Page Styles */
        .how-it-works-page {
          font-family: 'Montserrat', sans-serif;
          background: white;
        }
        
        .hiw-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .gold {
          color: #FFD700;
        }
        
        /* Hero Section */
        .hiw-hero {
          background: #0A0A0A;
          position: relative;
          padding: 80px 0;
          text-align: center;
        }
        
        .hiw-hero-overlay {
          position: absolute;
          inset: 0;
          background: url('/images/patterns/hero-pattern.png');
          opacity: 0.05;
          pointer-events: none;
        }
        
        .hiw-hero-content {
          position: relative;
          z-index: 2;
        }
        
        .hiw-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(255,215,0,0.1);
          padding: 6px 16px;
          border-radius: 30px;
          margin-bottom: 24px;
        }
        
        .hiw-badge-icon {
          height: 20px;
          width: auto;
        }
        
        .hiw-badge span {
          color: #FFD700;
          font-size: 14px;
        }
        
        .hiw-hero-title {
          font-size: 52px;
          font-weight: bold;
          color: white;
          margin-bottom: 20px;
        }
        
        .hiw-hero-subtitle {
          font-size: 18px;
          color: #ccc;
          max-width: 700px;
          margin: 0 auto 32px;
          line-height: 1.6;
        }
        
        .hiw-hero-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }
        
        .hiw-btn-primary {
          padding: 14px 32px;
          background: #FFD700;
          color: #0A0A0A;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .hiw-btn-primary:hover {
          background: #B8860B;
        }
        
        .hiw-btn-secondary {
          padding: 14px 32px;
          background: transparent;
          border: 2px solid #FFD700;
          color: #FFD700;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .hiw-btn-secondary:hover {
          background: #FFD700;
          color: #0A0A0A;
        }
        
        /* Section Titles */
        .hiw-section-title {
          font-size: 40px;
          text-align: center;
          margin-bottom: 20px;
          color: #0A0A0A;
        }
        
        .hiw-section-subtitle {
          font-size: 18px;
          text-align: center;
          color: #666;
          margin-bottom: 60px;
        }
        
        /* 5-Step Process Grid */
        .hiw-steps {
          padding: 80px 0;
          background: #F5F0E6;
        }
        
        .hiw-steps-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
        }
        
        .hiw-step-card {
          background: white;
          padding: 30px;
          border-radius: 15px;
          box-shadow: 0 5px 20px rgba(0,0,0,0.05);
          transition: transform 0.3s;
        }
        
        .hiw-step-card:hover {
          transform: translateY(-5px);
        }
        
        .hiw-step-number {
          font-size: 14px;
          color: #FFD700;
          font-weight: bold;
          margin-bottom: 10px;
        }
        
        .hiw-step-img {
          width: 100%;
          height: 200px;
          object-fit: cover;
          border-radius: 10px;
          margin-bottom: 20px;
        }
        
        .hiw-step-title {
          font-size: 22px;
          margin-bottom: 15px;
          color: #0A0A0A;
        }
        
        .hiw-step-desc {
          color: #555;
          line-height: 1.6;
          margin-bottom: 15px;
        }
        
        .hiw-step-tip {
          background: #F5F0E6;
          padding: 12px;
          border-radius: 8px;
          font-size: 13px;
          color: #666;
        }
        
        /* Features Section */
        .hiw-features {
          padding: 80px 0;
          background: white;
        }
        
        .hiw-features-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
        }
        
        .hiw-feature-card {
          background: #F5F0E6;
          padding: 40px 30px;
          text-align: center;
          border-radius: 10px;
          transition: transform 0.3s;
        }
        
        .hiw-feature-card:hover {
          transform: translateY(-5px);
        }
        
        .hiw-feature-icon {
          height: 70px;
          width: auto;
          margin-bottom: 20px;
        }
        
        .hiw-feature-card h3 {
          font-size: 20px;
          margin-bottom: 15px;
        }
        
        .hiw-feature-card p {
          color: #555;
          line-height: 1.6;
        }
        
        /* Death Flow Section */
        .hiw-death-flow {
          padding: 80px 0;
          background: #F5F0E6;
        }
        
        .hiw-flow-grid {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 20px;
          margin-bottom: 40px;
          flex-wrap: wrap;
        }
        
        .hiw-flow-grid.second-row {
          margin-bottom: 0;
        }
        
        .hiw-flow-card {
          background: white;
          padding: 30px;
          border-radius: 15px;
          flex: 1;
          min-width: 250px;
          text-align: center;
        }
        
        .hiw-flow-step {
          font-size: 12px;
          color: #FFD700;
          font-weight: bold;
          text-transform: uppercase;
          margin-bottom: 10px;
        }
        
        .hiw-flow-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }
        
        .hiw-flow-card h3 {
          font-size: 18px;
          margin-bottom: 10px;
        }
        
        .hiw-flow-card p {
          font-size: 14px;
          color: #555;
          line-height: 1.5;
        }
        
        .hiw-flow-arrow {
          font-size: 32px;
          color: #FFD700;
        }
        
        /* Testimonials */
        .hiw-testimonials {
          padding: 80px 0;
          background: white;
        }
        
        .hiw-testimonial-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
        }
        
        .hiw-testimonial-card {
          background: #F5F0E6;
          padding: 30px;
          border-radius: 10px;
        }
        
        .hiw-quote-icon {
          height: 40px;
          width: auto;
          opacity: 0.5;
          margin-bottom: 20px;
        }
        
        .hiw-testimonial-card p {
          font-size: 16px;
          font-style: italic;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        
        .hiw-author {
          display: flex;
          align-items: center;
          gap: 15px;
        }
        
        .hiw-author img {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          object-fit: cover;
        }
        
        .hiw-author h4 {
          font-size: 14px;
          margin-bottom: 3px;
        }
        
        .hiw-author p {
          font-size: 12px;
          color: #666;
          margin: 0;
          font-style: normal;
        }
        
        /* FAQ Section */
        .hiw-faq {
          padding: 80px 0;
          background: #F5F0E6;
        }
        
        .hiw-faq-grid {
          max-width: 800px;
          margin: 0 auto;
        }
        
        .hiw-faq-item {
          margin-bottom: 15px;
          border: 1px solid #eee;
          border-radius: 10px;
          overflow: hidden;
          background: white;
        }
        
        .hiw-faq-question {
          width: 100%;
          padding: 20px;
          background: white;
          border: none;
          text-align: left;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .hiw-faq-question:hover {
          background: #F5F0E6;
        }
        
        .hiw-faq-icon {
          font-size: 20px;
          color: #FFD700;
        }
        
        .hiw-faq-answer {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
          padding: 0 20px;
        }
        
        .hiw-faq-answer.open {
          max-height: 300px;
          padding: 20px;
          border-top: 1px solid #eee;
        }
        
        .hiw-faq-answer p {
          color: #555;
          line-height: 1.6;
        }
        
        .hiw-faq-more {
          text-align: center;
          margin-top: 30px;
        }
        
        .hiw-faq-link {
          color: #FFD700;
          text-decoration: none;
          font-weight: bold;
        }
        
        .hiw-faq-link:hover {
          text-decoration: underline;
        }
        
        /* CTA Section */
        .hiw-cta {
          padding: 80px 0;
          background: linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%);
          text-align: center;
        }
        
        .hiw-cta-content {
          max-width: 700px;
          margin: 0 auto;
        }
        
        .hiw-cta-title {
          font-size: 36px;
          color: white;
          margin-bottom: 20px;
        }
        
        .hiw-cta-subtitle {
          font-size: 18px;
          color: #ccc;
          margin-bottom: 30px;
          line-height: 1.5;
        }
        
        .hiw-cta-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }
        
        .hiw-cta-primary {
          padding: 14px 32px;
          background: #FFD700;
          color: #0A0A0A;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .hiw-cta-primary:hover {
          background: #B8860B;
        }
        
        .hiw-cta-secondary {
          padding: 14px 32px;
          background: transparent;
          border: 2px solid #FFD700;
          color: #FFD700;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .hiw-cta-secondary:hover {
          background: #FFD700;
          color: #0A0A0A;
        }
        
        .hiw-cta-note {
          font-size: 12px;
          color: #888;
        }
        
        /* Responsive */
        @media (max-width: 1024px) {
          .hiw-steps-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .hiw-features-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .hiw-testimonial-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .hiw-hero-title {
            font-size: 40px;
          }
        }
        
        @media (max-width: 768px) {
          .hiw-steps-grid {
            grid-template-columns: 1fr;
          }
          
          .hiw-features-grid {
            grid-template-columns: 1fr;
          }
          
          .hiw-testimonial-grid {
            grid-template-columns: 1fr;
          }
          
          .hiw-hero-title {
            font-size: 28px;
          }
          
          .hiw-hero-subtitle {
            font-size: 16px;
          }
          
          .hiw-section-title {
            font-size: 28px;
          }
          
          .hiw-flow-grid {
            flex-direction: column;
          }
          
          .hiw-flow-arrow {
            transform: rotate(90deg);
          }
          
          .hiw-cta-title {
            font-size: 24px;
          }
        }
        
      `}} />
      
    </div>
  );
};

export default HowItWorks;